import React from 'react';
import { Box } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../i18n/locale-format';
import { Contest } from '../../models/types';

const DAY_MS = 86400000;

const Stat = ({ label, value }: { label: string; value: number }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <Box component="span" sx={{ fontSize: 12, opacity: 0.8 }}>{label}</Box>
    <Box component="span" sx={{ fontSize: 22, fontWeight: 600 }}>{value ?? 0}</Box>
  </Box>
);

// Gradient hero for the contest ending soonest; elapsed-time progress is the only per-user-agnostic data available.
export const ActiveChallengeCard = ({ contest, extraCount, onOpen }: { contest: Contest; extraCount: number; onOpen: () => void }) => {
  const { t } = useTranslation('home');
  const start = new Date(contest.startAt).getTime();
  const end = new Date(contest.endAt).getTime();
  const now = Date.now();
  const daysLeft = Math.max(0, Math.ceil((end - now) / DAY_MS));
  const elapsed = end > start ? Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100)) : 100;
  const fmt = (ms: number) => formatDate(ms, { month: 'short', day: 'numeric' });

  return (
    <Box
      onClick={onOpen}
      role="link"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onOpen(); }}
      sx={{ cursor: 'pointer', background: 'var(--grad)', color: '#fff', borderRadius: '12px', p: '20px', display: 'flex', flexDirection: 'column', gap: '18px', overflow: 'hidden', minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ fontSize: 12, fontWeight: 500, backgroundColor: 'rgba(255,255,255,.18)', borderRadius: '999px', px: '10px', py: '4px', display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '9px', backgroundColor: '#86efac' }} />
          {t('activeChallenge.daysLeft', { count: daysLeft })}
        </Box>
        <ChevronRight />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <Box sx={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>{contest.name}</Box>
        <Box sx={{ fontSize: 13, opacity: 0.85 }}>{[contest.contestType, contest.activityType].filter(Boolean).join(' · ')}</Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <Stat label={t('activeChallenge.participants')} value={contest.numberOfParticipants} />
        {contest.contestType === 'Team' && <Stat label={t('activeChallenge.teams')} value={contest.numberOfTeams} />}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ height: 8, borderRadius: '99px', backgroundColor: 'rgba(255,255,255,.22)', overflow: 'hidden' }}>
          <Box sx={{ width: `${elapsed}%`, height: '100%', backgroundColor: '#fff', borderRadius: '99px' }} />
        </Box>
        <Box sx={{ fontSize: 12, opacity: 0.9 }}>
          {fmt(start)} – {fmt(end)}{extraCount > 0 ? ` · ${t('activeChallenge.moreActive', { count: extraCount })}` : ''}
        </Box>
      </Box>
    </Box>
  );
};
